import React from 'react';
import { ColumnLayout } from '@amzn/awsui-components-react-v3';
import { AccuracyButtonNewStyle } from '../../../styles/common_styles';
import { TrendFilter } from '../../buttons/Trend_Filter';
import { TrendReportChartTitle } from './trend/trend_report_chart_title';
import { TrendReportChart } from './trend/trend_report_chart';
import { CHART_TYPE, GOAL_KEY, MARKETPLACE } from '../../../constants/trend_report';
import { extractFromCompositeKey, searchCompositeKey, extractFromCompositeKeys } from '../utils/composite_keys';
import { SNAPSHOT_ID, KEY_SEPARATOR } from '../common/constants';

const accuracyTypeIdLabelMap = {
  cpAccuracy: 'Accuracy',
  transactionAccuracy: 'Transactional Accuracy',
  standardRateAccuracy: 'Standard Rate Accuracy',
  predictiveAccuracy: 'Predictive Accuracy',
};

const answerTypeIdLabelMap = {
  firstCpAccuracy: 'First Answer',
  lastCpAccuracy: 'Last Answer',
  firstCpAvAccuracy: 'First Answer(Abs)',
  lastCpAvAccuracy: 'Last Answer(Abs)',
  firstCpNvAccuracy: 'First Answer(Net)',
  lastCpNvAccuracy: 'Last Answer(Net)',
};

export class TrendSummary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      displayedChart: CHART_TYPE.LINE,
    };
    this.accuracyOptions = [];
    this.channelOptions = [];
    this.snapshotOptions = [];
    this.aggregatedOptions = [];
    this.data = {};
    this.allLinesId = [];
    this.aggregatedField = '';
  }

  formatChartLabel = (key) => {
    const [compositeKey, originalKey] = key.split(KEY_SEPARATOR);
    let label = `${this.getLabelPrefix(compositeKey)}${this.getLabel(originalKey)}`;
    return key.includes(GOAL_KEY) ? `${GOAL_KEY}-${label}` : `${label}`;
  };

  getLabel = (originalKey) => {
    let [channel, answerType, accuracyType] = originalKey.split('-');
    return answerType === 'perUnitVariance'
      ? `Net CP Per Unit Variance-${channel}`
      : `${answerTypeIdLabelMap[answerType]} ${accuracyTypeIdLabelMap[accuracyType]}-${channel}`;
  };

  getLabelPrefix = (compositeKey) => {
    let prefix = '';
    if (this.props.shownSnapshots.length > 1) {
      prefix = `[${extractFromCompositeKey(compositeKey, SNAPSHOT_ID)}]`;
    } else if (this.aggregatedField.includes(MARKETPLACE)) {
      const marketplace = this.props.marketplaceOptions.filter(
        (mp) => mp.value === extractFromCompositeKey(compositeKey, MARKETPLACE)
      )[0];
      prefix = `[${marketplace.label}]`;
    }
    return prefix;
  };

  onToggleChart = () => {
    this.setState({ displayedChart: this.state.displayedChart === CHART_TYPE.LINE ? CHART_TYPE.BAR : CHART_TYPE.LINE });
  };

  getShownIds = () => {
    let ids = [];
    this.props.shownChannels.map((channel) => {
      this.props.shownAccuracyKeys.map((accuracyType) => {
        this.props.shownSnapshots.map((snapshotIndex) => {
          if (this.aggregatedField) {
            this.props.shownAggregatedItems.map((item) => {
              ids.push(
                ...this.getReportIds(
                  searchCompositeKey(item, this.props.reportData.compositeKeys, this.aggregatedField),
                  channel,
                  accuracyType
                )
              );
            });
          } else {
            ids.push(...this.getReportIds(this.props.reportData.compositeKeys[snapshotIndex], channel, accuracyType));
          }
        });
      });
    });
    return ids;
  };

  getReportIds = (compositeKey, channel, accuracyType) => {
    let ids = [];
    let key = compositeKey + KEY_SEPARATOR + [channel, accuracyType].join('-');
    ids.push(key);
    if (accuracyType.includes('firstCpAvAccuracy')) {
      ids.push([key, GOAL_KEY].join('-'));
    }
    return ids;
  };

  getSnapshotDropdownOptions = () => {
    const { snapshots } = this.props;
    return snapshots.map((snapshot, index) => {
      return { label: snapshot.id === this.props.appConfig.liveSnapshotId ? 'live' : snapshot.name, value: index };
    });
  };

  getChannelDropdownOptions = () => {
    return this.props.reportData.allChannels.map((channel) => {
      return { label: channel, value: channel };
    });
  };

  getSummaryAccuracyDropdownOptions = () => {
    const { summaryMapKeys } = this.props;
    let accuracyTypeDropdownOptions = [];
    Object.keys(answerTypeIdLabelMap)
      .filter((k) => summaryMapKeys.includes(k))
      .map((answerType) => {
        Object.keys(accuracyTypeIdLabelMap).map((accuracyType) => {
          let accuracyTypeKey = [answerType, accuracyType].join('-');
          accuracyTypeDropdownOptions.push({
            label: this.formatSummaryAccuracyTypesLabel(accuracyTypeKey),
            value: accuracyTypeKey,
          });
        });
      });
    accuracyTypeDropdownOptions.unshift(
    {
      label: 'Net CP Per Unit Variance(Contribution Profit)',
      value: 'perUnitVariance- ',
    });
    return accuracyTypeDropdownOptions;
  };

  getAggregatedDropdownOptions = () => {
    const aggregatedFields = this.props.reportData.aggregatedFields;
    return aggregatedFields
      .map((field) => {
        const fieldValues = extractFromCompositeKeys(this.props.reportData.compositeKeys, field);
        return this.props[`${field}Options`].filter((op) => fieldValues.includes(op.value));
      })
      .flat();
  };

  getDropdownOptions = () => {
    this.accuracyOptions = this.getSummaryAccuracyDropdownOptions();
    this.channelOptions = this.getChannelDropdownOptions();
    this.snapshotOptions = this.getSnapshotDropdownOptions();
    this.aggregatedOptions = this.getAggregatedDropdownOptions();
  };

  formatSummaryAccuracyTypesLabel = (accuracyTypeKey) => {
    let [answerType, accuracyType] = accuracyTypeKey.split('-');
    return [answerTypeIdLabelMap[answerType], accuracyTypeIdLabelMap[accuracyType]].join(' ');
  };

  updateShownSummaryChannel = (chosen) => {
    this.props.updateSelectedOptions(chosen, 'shownSummaryChannels');
  };

  updateShownSummaryMetrics = (chosen) => {
    this.props.updateSelectedOptions(chosen, 'shownSummaryMetrics');
  };

  updateShownSummarySnapshots = (chosen) => {
    this.props.updateSelectedOptions(chosen, 'shownSummarySnapshots');
  };

  updateShownSummaryAggregates = (chosen) => {
    this.props.updateSelectedOptions(chosen, 'shownSummaryAggregatedItems');
  };

  onDownloadDisplayedData = () => {
    this.props.downloadDisplayedData(
      this.data,
      this.allLinesId,
      `Summary Trend Data_${new Date(Date.now()).toLocaleString()}.xls`,
      this.formatChartLabel
    );
  };

  render() {
    if (this.props.reportData) {
      this.data = this.props.getDataForSharedMonths(
        this.props.reportData.processedSummaryData,
        this.props.shownSnapshots
      );
      this.allLinesId = this.getShownIds();
      this.getDropdownOptions();
      this.aggregatedField = this.props.reportData.aggregatedFields.length
        ? this.props.reportData.aggregatedFields[0]
        : '';
    }

    return (
      <div key={1} className="awsui-util-container">
        <TrendReportChartTitle
          text={this.props.title}
          allLinesId={this.allLinesId}
          chartType={this.state.displayedChart}
          onChangeFn={this.onToggleChart}
          onDownloadDisplayedData={this.onDownloadDisplayedData}
          data={this.data}
        />
        <ColumnLayout columns={3}>
            <div>
              <TrendFilter
                text="Channel"
                classname={AccuracyButtonNewStyle}
                options={this.channelOptions}
                onChange={this.updateShownSummaryChannel}
                selectedIds={this.props.shownChannels}
              />
            </div>
            <div>
              <TrendFilter
                text="Accuracy/Per Unit Variance"
                classname="AccuracyTypeMultiSelect"
                options={this.accuracyOptions}
                onChange={this.updateShownSummaryMetrics}
                selectedIds={this.props.shownAccuracyKeys}
              />
            </div>
            {this.aggregatedField && this.aggregatedField.includes(MARKETPLACE) && (
              <div>
                <TrendFilter
                  text="Marketplaces"
                  classname={AccuracyButtonNewStyle}
                  options={this.aggregatedOptions}
                  onChange={this.updateShownSummaryAggregates}
                  selectedIds={this.props.shownAggregatedItems}
                />
              </div>
            )}
            {!this.aggregatedField && (
              <div>
                <TrendFilter
                  text="Snapshots"
                  classname={AccuracyButtonNewStyle}
                  options={this.snapshotOptions}
                  onChange={this.updateShownSummarySnapshots}
                  selectedIds={this.props.shownSnapshots}
                />
              </div>
            )}
        </ColumnLayout>
        <TrendReportChart
          chartType={this.state.displayedChart}
          bothAccuracyAndVarianceSelected={this.props.bothAccuracyAndVarianceSelected(this.props.shownAccuracyKeys)}
          allLinesId={this.allLinesId}
          labelFormatter={this.formatChartLabel}
          data={this.data}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/reports/components/trend_summary.js